<!-- resources/js/Components/CookieBanner.vue -->
<template>
    <!-- Banner principal -->
    <transition name="slide-down">
        <div v-if="showBanner"
            class="fixed bottom-0 left-0 right-0 bg-blue-800 text-white py-4 px-4 sm:px-6 z-50 shadow-lg"
            role="alertdialog" aria-labelledby="cookie-banner-title" aria-describedby="cookie-banner-desc">
            <div class="container mx-auto max-w-7xl flex flex-col sm:flex-row justify-between items-center gap-3">
                <div class="flex-1 text-sm text-center sm:text-left mb-4 sm:mb-0" id="cookie-banner-desc">
                    <p class="inline">
                        Prefeitura Municipal de Parnaíba utiliza cookies para melhorar a sua
                        experiência, de acordo com a nossa
                        <a href="/politica-privacidade" class="underline hover:text-blue-200 font-medium ml-1"
                            target="_blank" rel="noopener noreferrer">
                            Política de Privacidade
                        </a>
                    </p>
                </div>

                <div class="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <button @click="declineCookies" id="decline-btn"
                        class="btn btn-sm sm:btn-md bg-gray-600 hover:bg-gray-700 text-white border-0 w-full sm:w-auto">
                        Rejeitar Tudo
                    </button>
                    <!-- <button @click="openSettings" id="configure-btn"
                        class="btn btn-sm sm:btn-md bg-blue-700 hover:bg-blue-600 text-white border-0 w-full sm:w-auto">
                        Salvar Preferências
                    </button> -->
                    <button @click="acceptAllCookies" id="accept-all-btn"
                        class="btn btn-sm sm:btn-md bg-white hover:bg-gray-100 text-blue-800 border-0 w-full sm:w-auto">
                        Aceitar Tudo
                    </button>
                </div>
            </div>
        </div>
    </transition>

    <div v-if="showModal" class="modal modal-open">
        <div class="modal-box max-w-2xl">
            <div class="flex justify-between items-center mb-6">
                <h3 class="font-bold text-xl">Personalizar Preferências de Consentimento</h3>
                <button @click="closeModal" class="btn btn-sm btn-circle btn-ghost">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24"
                        stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                            d="M6 18L18 6M6 6l12 12" />
                    </svg>
                </button>
            </div>

            <p class="text-gray-600 mb-6">
                Utilizamos cookies para ajudá-lo a navegar de forma eficiente e executar certas funções.
                Você encontrará informações detalhadas sobre todos os cookies abaixo.
            </p>

            <div v-for="category in categories" :key="category.id" class="mb-6 border-b pb-4 last:border-b-0 last:pb-0">
                <div class="flex justify-between items-center mb-2">
                    <h4 class="font-bold text-lg">{{ category.name }}</h4>
                    <span v-if="category.alwaysActive" class="badge badge-success">Sempre Ativo</span>
                </div>
                <p class="text-gray-600 text-sm mb-3">{{ category.description }}</p>

                <div v-if="!category.alwaysActive" class="flex justify-end mt-2">
                    <label class="cursor-pointer label">
                        <input type="checkbox" v-model="selectedPreferences[category.id]" class="toggle toggle-primary"
                            :disabled="category.alwaysActive">
                    </label>
                </div>
            </div>

            <div class="modal-action mt-8 flex flex-col sm:flex-row gap-3">
                <button @click="rejectAll" class="btn btn-primary w-full sm:w-auto">
                    Rejeitar Tudo
                </button>
                <!-- <button @click="savePreferences" class="btn btn-primary w-full sm:w-auto">
                    Salvar Preferências
                </button> -->
                <button @click="acceptAll" class="btn btn-primary w-full sm:w-auto">
                    Aceitar Tudo
                </button>
            </div>
        </div>

        <div class="modal-backdrop" @click="closeModal"></div>
    </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'

const showBanner = ref(false)
const showModal = ref(false)
const COOKIE_NAME = 'cookies-accepted'
const COOKIE_PREFERENCES = 'cookie-preferences'

// Definição das categorias de cookies
const categories = ref([
    {
        id: 'necessary',
        name: 'Necessário',
        description: 'Cookies necessários são exigidos para habilitar as funcionalidades básicas do site, como fornecer login seguro ou ajustar suas preferências de consentimento. Esses cookies não armazenam dados pessoalmente identificáveis.',
        alwaysActive: true,
        enabled: true
    },
    {
        id: 'functional',
        name: 'Funcional',
        description: 'Cookies funcionais ajudam a executar certas funcionalidades, como compartilhar o conteúdo do site em redes sociais, coletar feedback e outras funcionalidades de terceiros.',
        alwaysActive: false,
        enabled: true
    },
    {
        id: 'analytics',
        name: 'Analíticos',
        description: 'Cookies analíticos são usados para entender como os visitantes interagem com o site. Esses cookies ajudam a fornecer informações sobre métricas como número de visitantes, taxa de rejeição, fonte de tráfego, etc.',
        alwaysActive: false,
        enabled: true
    },
    {
        id: 'performance',
        name: 'Desempenho',
        description: 'Cookies de desempenho são usados para entender e analisar os principais índices de desempenho do site, ajudando a entregar uma melhor experiência ao usuário.',
        alwaysActive: false,
        enabled: false
    },
    {
        id: 'advertisement',
        name: 'Publicidade',
        description: 'Cookies publicitários são usados para fornecer aos visitantes anúncios personalizados com base nas páginas que visitaram anteriormente e para analisar a eficácia das campanhas publicitárias.',
        alwaysActive: false,
        enabled: true
    }
])

// Preferências selecionadas pelo usuário
const selectedPreferences = ref({})

// Inicializa as preferências com os valores padrão
const initializePreferences = () => {
    selectedPreferences.value = {}
    categories.value.forEach(cat => {
        selectedPreferences.value[cat.id] = cat.enabled
    })
}

// Verifica se já aceitou os cookies
onMounted(() => {
    try {
        const cookies = document.cookie.split('; ')
        const hasAccepted = cookies.some(cookie =>
            cookie.startsWith(`${COOKIE_NAME}=`)
        )
        showBanner.value = !hasAccepted

        // Carrega preferências existentes
        const preferencesCookie = cookies.find(cookie =>
            cookie.startsWith(`${COOKIE_PREFERENCES}=`)
        )

        if (preferencesCookie) {
            const preferences = JSON.parse(decodeURIComponent(preferencesCookie.split('=')[1]))
            categories.value.forEach(cat => {
                if (preferences[cat.id] !== undefined) {
                    cat.enabled = preferences[cat.id]
                }
            })
        }
    } catch (error) {
        console.error('Erro ao verificar cookies:', error)
        showBanner.value = true
    }
})

// Funções do banner
const acceptAllCookies = () => {
    categories.value.forEach(cat => {
        if (!cat.alwaysActive) {
            cat.enabled = true
        }
    })
    saveCookiePreferences()
    showBanner.value = false
}

const declineCookies = () => {
    categories.value.forEach(cat => {
        if (!cat.alwaysActive) {
            cat.enabled = false
        }
    })
    saveCookiePreferences()
    showBanner.value = false
}

const openSettings = () => {
    initializePreferences()
    showModal.value = true
}

const closeModal = () => {
    showModal.value = false
}

// Funções do modal
const rejectAll = () => {
    categories.value.forEach(cat => {
        if (!cat.alwaysActive) {
            selectedPreferences.value[cat.id] = false
        }
    })
}

const acceptAll = () => {
    categories.value.forEach(cat => {
        if (!cat.alwaysActive) {
            selectedPreferences.value[cat.id] = true
        }
    })
}

const savePreferences = () => {
    // Atualiza as preferências globais
    categories.value.forEach(cat => {
        if (!cat.alwaysActive) {
            cat.enabled = selectedPreferences.value[cat.id]
        }
    })

    saveCookiePreferences()
    closeModal()
}

// Salva as preferências em cookies
const saveCookiePreferences = () => {
    try {
        const date = new Date()
        date.setFullYear(date.getFullYear() + 1)

        // Salva preferências
        const preferences = {}
        categories.value.forEach(cat => {
            preferences[cat.id] = cat.enabled
        })

        document.cookie = `${COOKIE_PREFERENCES}=${encodeURIComponent(JSON.stringify(preferences))}; expires=${date.toUTCString()}; path=/; SameSite=Lax`
        document.cookie = `${COOKIE_NAME}=true; expires=${date.toUTCString()}; path=/; SameSite=Lax`

        // Dispara evento global
        window.dispatchEvent(new Event('cookies-accepted'))
    } catch (error) {
        console.error('Erro ao salvar preferências de cookies:', error)
    }
}
</script>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.slide-down-enter-from,
.slide-down-leave-to {
    transform: translateY(100%);
    opacity: 0;
}
</style>
